import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  LinearProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';

interface SmsCampaign {
  id: string;
  name: string;
  description: string;
  message: string;
  channel: string;
  crm_stage_id?: string;
  scheduled_at?: string;
  settings: {
    enablePersonalization: boolean;
    enableTracking: boolean;
    enableRetry: boolean;
    maxRetries: number;
    delayBetweenMessages: number;
    sendInBatches: boolean;
    batchSize: number;
    batchDelay: number;
    senderName?: string;
    priority: 'low' | 'normal' | 'high';
  };
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
}

interface CampaignStats {
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  pending: number;
  retry: number;
  cost: number;
}

const SmsList: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<SmsCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<SmsCampaign | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [campaignStats, setCampaignStats] = useState<{[key: string]: CampaignStats}>({});
  const [loadingStats, setLoadingStats] = useState<{[key: string]: boolean}>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [spendingPeriod, setSpendingPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'all'>('all');
  const [spending, setSpending] = useState<{total: number; totalSms: number; period: string} | null>(null);
  const [loadingSpending, setLoadingSpending] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchSpending();
  }, []);

  useEffect(() => {
    fetchSpending();
  }, [spendingPeriod]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/sms-campaigns');
      setCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignStats = async (campaignId: string) => {
    try {
      setLoadingStats(prev => ({ ...prev, [campaignId]: true }));
      const response = await axios.get(`/api/v1/sms-campaigns/${campaignId}/stats`);
      setCampaignStats(prev => ({ ...prev, [campaignId]: response.data.data }));
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    } finally {
      setLoadingStats(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const fetchSpending = async () => {
    try {
      setLoadingSpending(true);
      const response = await axios.get(`/api/v1/sms-campaigns/spending?period=${spendingPeriod}`);
      setSpending(response.data.data);
    } catch (error) {
      console.error('Erro ao buscar gastos:', error);
    } finally {
      setLoadingSpending(false);
    }
  };

  const handleAction = async (campaignId: string, action: string) => {
    try {
      setActionLoading(campaignId);
      let endpoint = '';
      
      switch (action) {
        case 'start':
          endpoint = `/api/v1/sms-campaigns/${campaignId}/start`;
          break;
        case 'pause':
          endpoint = `/api/v1/sms-campaigns/${campaignId}/pause`;
          break;
        case 'stop':
          endpoint = `/api/v1/sms-campaigns/${campaignId}/stop`;
          break;
        case 'delete':
          endpoint = `/api/v1/sms-campaigns/${campaignId}`;
          break;
      }

      if (action === 'delete') {
        await axios.delete(endpoint);
      } else {
        await axios.post(endpoint);
      }

      // Recarregar campanhas
      await fetchCampaigns();
      
      // Se a ação foi start, buscar estatísticas
      if (action === 'start') {
        await fetchCampaignStats(campaignId);
      }
    } catch (error) {
      console.error(`Erro ao ${action} campanha:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSchedule = async () => {
    if (!selectedCampaign || !scheduledAt) return;

    try {
      setActionLoading(selectedCampaign.id);
      await axios.post(`/api/v1/sms-campaigns/${selectedCampaign.id}/schedule`, {
        scheduledAt: new Date(scheduledAt).toISOString()
      });
      
      await fetchCampaigns();
      setShowScheduleDialog(false);
      setScheduledAt('');
    } catch (error) {
      console.error('Erro ao agendar campanha:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'scheduled': return 'info';
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <MessageIcon />;
      case 'scheduled': return <ScheduleIcon />;
      case 'active': return <PlayIcon />;
      case 'paused': return <PauseIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <MessageIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getProgressPercentage = (stats: CampaignStats) => {
    if (stats.total === 0) return 0;
    return Math.round(((stats.sent + stats.delivered + stats.failed) / stats.total) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/campaigns')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Campanhas SMS
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/campaigns/sms/create')}
        >
          Nova Campanha
        </Button>
      </Box>

      {/* Dashboard Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MessageIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{campaigns.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Campanhas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PlayIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {campaigns.filter(c => c.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campanhas Ativas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {campaigns.filter(c => c.status === 'scheduled').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campanhas Agendadas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {campaigns.filter(c => c.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campanhas Concluídas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Card de Gastos SMS */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" component="h2">
                  Gastos com SMS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Custo por SMS: R$ 0,08
                </Typography>
              </Box>
            </Box>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={spendingPeriod}
                label="Período"
                onChange={(e) => setSpendingPeriod(e.target.value as 'daily' | 'weekly' | 'monthly' | 'all')}
              >
                <MenuItem value="daily">Diário</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensal</MenuItem>
                <MenuItem value="all">Geral</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {loadingSpending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : spending ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: '#1976d2', 
                    borderRadius: 2,
                    color: '#000000',
                    boxShadow: 2
                  }}
                >
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {spending.totalSms.toLocaleString('pt-BR')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    SMS Enviados
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: '#2e7d32', 
                    borderRadius: 2,
                    color: '#000000',
                    boxShadow: 2
                  }}
                >
                  <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                    R$ {(spending.totalSms * 0.08).toFixed(2).replace('.', ',')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Custo Estimado
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">Nenhum dado de gastos disponível</Alert>
          )}
        </CardContent>
      </Card>

      {/* Campanhas Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lista de Campanhas
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : campaigns.length === 0 ? (
            <Alert severity="info">
              Nenhuma campanha SMS encontrada. Crie sua primeira campanha!
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell>Criada em</TableCell>
                    <TableCell>Progresso</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => {
                    const stats = campaignStats[campaign.id];
                    const progress = stats ? getProgressPercentage(stats) : 0;
                    
                    return (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{campaign.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {campaign.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(campaign.status)}
                            label={campaign.status}
                            color={getStatusColor(campaign.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {campaign.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(campaign.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: '100%', maxWidth: 200 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={progress} 
                                sx={{ flexGrow: 1, mr: 1 }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {progress}%
                              </Typography>
                            </Box>
                            {stats && (
                              <Typography variant="caption" color="text.secondary">
                                {stats.sent + stats.delivered + stats.failed}/{stats.total} enviados
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedCampaign(campaign);
                                  setShowDetailsDialog(true);
                                  if (!stats) {
                                    fetchCampaignStats(campaign.id);
                                  }
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            
                            {campaign.status === 'draft' && (
                              <>
                                <Tooltip title="Iniciar campanha">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={actionLoading === campaign.id}
                                    onClick={() => handleAction(campaign.id, 'start')}
                                  >
                                    {actionLoading === campaign.id ? <CircularProgress size={16} /> : <PlayIcon />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Agendar campanha">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => {
                                      setSelectedCampaign(campaign);
                                      setShowScheduleDialog(true);
                                    }}
                                  >
                                    <ScheduleIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            
                            {campaign.status === 'active' && (
                              <Tooltip title="Pausar campanha">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={actionLoading === campaign.id}
                                  onClick={() => handleAction(campaign.id, 'pause')}
                                >
                                  {actionLoading === campaign.id ? <CircularProgress size={16} /> : <PauseIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {campaign.status === 'paused' && (
                              <Tooltip title="Retomar campanha">
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={actionLoading === campaign.id}
                                  onClick={() => handleAction(campaign.id, 'start')}
                                >
                                  {actionLoading === campaign.id ? <CircularProgress size={16} /> : <PlayIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {(campaign.status === 'active' || campaign.status === 'paused') && (
                              <Tooltip title="Parar campanha">
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={actionLoading === campaign.id}
                                  onClick={() => handleAction(campaign.id, 'stop')}
                                >
                                  {actionLoading === campaign.id ? <CircularProgress size={16} /> : <StopIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Excluir campanha">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={actionLoading === campaign.id}
                                onClick={() => handleAction(campaign.id, 'delete')}
                              >
                                {actionLoading === campaign.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Detalhes */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes da Campanha: {selectedCampaign?.name}
        </DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Informações Gerais</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Nome</Typography>
                    <Typography variant="body1">{selectedCampaign.name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Descrição</Typography>
                    <Typography variant="body1">{selectedCampaign.description}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      icon={getStatusIcon(selectedCampaign.status)}
                      label={selectedCampaign.status}
                      color={getStatusColor(selectedCampaign.status) as any}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Criada em</Typography>
                    <Typography variant="body1">{formatDate(selectedCampaign.created_at)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Mensagem</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedCampaign.message}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Estatísticas</Typography>
                  {loadingStats[selectedCampaign.id] ? (
                    <CircularProgress />
                  ) : campaignStats[selectedCampaign.id] ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h4" color="primary">
                              {campaignStats[selectedCampaign.id].total}
                            </Typography>
                            <Typography variant="body2">Total</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h4" color="success.main">
                              {campaignStats[selectedCampaign.id].delivered}
                            </Typography>
                            <Typography variant="body2">Entregues</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h4" color="warning.main">
                              {campaignStats[selectedCampaign.id].sent}
                            </Typography>
                            <Typography variant="body2">Enviadas</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="h4" color="error.main">
                              {campaignStats[selectedCampaign.id].failed}
                            </Typography>
                            <Typography variant="body2">Falharam</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info">Nenhuma estatística disponível</Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Agendamento */}
      <Dialog
        open={showScheduleDialog}
        onClose={() => setShowScheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agendar Campanha</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="datetime-local"
            label="Data e Hora do Envio"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScheduleDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleSchedule}
            variant="contained"
            disabled={!scheduledAt || actionLoading === selectedCampaign?.id}
          >
            {actionLoading === selectedCampaign?.id ? <CircularProgress size={20} /> : 'Agendar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmsList; 